import { useState, useEffect, useCallback } from "react";
import { getPortfolioHoldings, type AggregatedHolding } from "../../services/portfolioService";

interface HoldingsTabProps {
  accountId?: string;
}

export function HoldingsTab({ accountId }: HoldingsTabProps) {
  const [holdings, setHoldings] = useState<AggregatedHolding[]>([]);
  const [totalValue, setTotalValue] = useState("0.00");
  const [totalCostBasis, setTotalCostBasis] = useState("0.00");
  const [totalUnrealized, setTotalUnrealized] = useState("0.00");
  const [, setTotalTodayPnL] = useState("0.00");
  const [accountName, setAccountName] = useState("All Accounts");
  const [sortBy, setSortBy] = useState<"value" | "allocation" | "change">("value");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadHoldings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getPortfolioHoldings(accountId);
      setHoldings(data.holdings || []);
      setTotalValue(data.total_value || "0.00");
      setTotalCostBasis(data.total_cost_basis || "0.00");
      setTotalUnrealized(data.total_unrealized || "0.00");
      setTotalTodayPnL(data.total_today_pnl || "0.00");
      setAccountName(data.account_name || "All Accounts");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load holdings");
    } finally {
      setIsLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    loadHoldings();
  }, [loadHoldings]);

  const formatCurrency = (value: string | number | undefined | null) => {
    const num = typeof value === "string" ? parseFloat(value) : (typeof value === "number" ? value : 0);
    if (isNaN(num)) return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const formatNumber = (value: string | number | undefined | null, decimals = 4) => {
    const num = typeof value === "string" ? parseFloat(value) : (typeof value === "number" ? value : 0);
    if (isNaN(num)) return "0";
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: decimals,
    });
  };

  const formatPercent = (value: string | number | undefined | null) => {
    const num = typeof value === "string" ? parseFloat(value) : (typeof value === "number" ? value : 0);
    if (isNaN(num)) return "0.00%";
    return `${num >= 0 ? "+" : ""}${num.toFixed(2)}%`;
  };

  const handleSort = (column: "value" | "allocation" | "change") => {
    if (sortBy === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDirection("desc");
    }
  };

  const sortedHoldings = [...holdings].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case "value":
        comparison = parseFloat(a.current_value) - parseFloat(b.current_value);
        break;
      case "allocation":
        comparison = parseFloat(a.weight_pct) - parseFloat(b.weight_pct);
        break;
      case "change":
        comparison = parseFloat(a.today_pnl_pct) - parseFloat(b.today_pnl_pct);
        break;
    }
    return sortDirection === "asc" ? comparison : -comparison;
  });

  const SortIcon = ({ column }: { column: "value" | "allocation" | "change" }) => {
    if (sortBy !== column) {
      return (
        <svg className="w-3 h-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d={sortDirection === "asc" ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"}
        />
      </svg>
    );
  };

  if (isLoading) {
    return (
      <div className="glass-card p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-48 bg-surface-700 rounded" />
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-surface-800 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-card p-6">
        <div className="text-center py-12">
          <svg className="w-12 h-12 mx-auto mb-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div className="text-red-400 mb-3">{error}</div>
          <button
            onClick={loadHoldings}
            className="px-4 py-2 bg-surface-700 hover:bg-surface-600 rounded-lg text-sm transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (holdings.length === 0) {
    return (
      <div className="glass-card p-6">
        <div className="text-center py-12 text-surface-400">
          <svg className="w-16 h-16 mx-auto mb-4 text-surface-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <p className="text-lg font-medium mb-1">No holdings yet</p>
          <p className="text-sm">Execute strategies to see your aggregated holdings here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-surface-700/50">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-surface-100">Holdings - {accountName}</h2>
            <p className="text-sm text-surface-400 mt-1">
              Real-time positions directly from Alpaca
            </p>
          </div>
          <div className="text-right">
            <div className="text-sm text-surface-400">Total Value</div>
            <div className="text-2xl font-bold text-surface-100">{formatCurrency(totalValue)}</div>
            <div className={`text-sm mt-1 ${parseFloat(totalUnrealized) >= 0 ? "text-green-400" : "text-red-400"}`}>
              {formatCurrency(totalUnrealized)} ({formatPercent(parseFloat(totalUnrealized) / parseFloat(totalCostBasis || "1") * 100)})
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-xs text-surface-400 bg-surface-800/50">
              <th className="py-3 px-6 font-medium">Asset</th>
              <th className="py-3 px-4 font-medium text-right">Qty</th>
              <th className="py-3 px-4 font-medium text-right">Side</th>
              <th className="py-3 px-4 font-medium text-right">Price</th>
              <th className="py-3 px-4 font-medium text-right">Cost Basis</th>
              <th className="py-3 px-4 font-medium">
                <button
                  onClick={() => handleSort("value")}
                  className="flex items-center gap-1 ml-auto hover:text-surface-200 transition-colors"
                >
                  Current Value
                  <SortIcon column="value" />
                </button>
              </th>
              <th className="py-3 px-4 font-medium">
                <button
                  onClick={() => handleSort("change")}
                  className="flex items-center gap-1 ml-auto hover:text-surface-200 transition-colors"
                >
                  Today's P/L (%)
                  <SortIcon column="change" />
                </button>
              </th>
              <th className="py-3 px-6 font-medium">
                <button
                  onClick={() => handleSort("allocation")}
                  className="flex items-center gap-1 ml-auto hover:text-surface-200 transition-colors"
                >
                  Weight
                  <SortIcon column="allocation" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-800">
            {sortedHoldings.map((holding) => {
              const unrealizedPnL = parseFloat(holding.unrealized_pnl);
              const todayPnLPct = parseFloat(holding.today_pnl_pct);
              const weightPct = parseFloat(holding.weight_pct);

              return (
                <tr
                  key={holding.symbol}
                  className="hover:bg-surface-800/30 transition-colors"
                >
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500/20 to-primary-600/10 flex items-center justify-center">
                        <span className="text-primary-400 text-sm font-bold">
                          {holding.symbol.slice(0, 2)}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-surface-100">{holding.symbol}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-right text-surface-300">
                    {formatNumber(holding.quantity)}
                  </td>
                  <td className="py-4 px-4 text-right text-surface-300">
                    <span className={`px-2 py-1 rounded text-xs ${holding.side === "Long" ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
                      {holding.side}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-right text-surface-200">
                    {formatCurrency(holding.current_price)}
                  </td>
                  <td className="py-4 px-4 text-right text-surface-300">
                    {formatCurrency(holding.cost_basis)}
                  </td>
                  <td className="py-4 px-4 text-right font-medium text-surface-100">
                    {formatCurrency(holding.current_value)}
                    <div className={`text-xs mt-1 ${unrealizedPnL >= 0 ? "text-green-400" : "text-red-400"}`}>
                      {formatCurrency(holding.unrealized_pnl)} ({formatPercent(holding.unrealized_pnl_pct)})
                    </div>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <div className={todayPnLPct >= 0 ? "text-green-400" : "text-red-400"}>
                      <div>{formatCurrency(holding.today_pnl)}</div>
                      <div className="text-xs">{formatPercent(todayPnLPct)}</div>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center justify-end gap-3">
                      <div className="w-24 h-2 rounded-full bg-surface-700 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-primary-500 to-primary-400"
                          style={{ width: `${Math.min(weightPct, 100)}%` }}
                        />
                      </div>
                      <span className="text-surface-200 text-sm min-w-[55px] text-right">
                        {weightPct.toFixed(1)}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
